/**
 * Cube physics math — deterministic evaluation of the cross-screen cube.
 *
 * The whole simulation is a pure function of server time: the server
 * broadcasts a tiny state (epoch, position, velocity, target), every screen
 * evaluates position(tServer) locally via its clock-ping offset. No position
 * streaming, zero drift. specs/screen-mgmnt.md "Cross-screen cube physics".
 *
 * NB: apps/backend/src/services/cubePhysicsMath.js is this file's twin (the
 * backend Docker image can't reach packages/shared) — keep them in step.
 *
 * World: the room unrolled into a strip. x in 1920-scale px across all 8
 * screens + gaps; y in px above the floor (0 = floor, 1080 = ceiling).
 *
 * Modes:
 *  - roam: constant vx reflecting off world ends; bounce apex h0
 *  - steer (after touch): critically damped x → xT, apex eases h0 → hT,
 *    end state bounces in place at (xT, apex hT)
 */

export const SCREEN_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'D1', 'D2'];
export const SCREEN_W = 1920;
export const SCREEN_H = 1080;
export const CUBE_SIZE = 240;

export const DEFAULT_GAPS = { bezel: 120, corner: 600 };

export const DEFAULT_STATE = (now = 0) => ({
  mode: 'roam',
  t0: now,          // server-clock ms
  x0: SCREEN_W / 2, // world px
  vx0: 140,         // px/s
  xT: null,
  h0: 420,          // bounce apex px
  hT: null,
  w: 1.1,           // damped steering natural frequency (rad/s)
  g: 900,           // gravity px/s^2
  phase0: 0,        // bounce-cycle phase carried across retargets
});

export function screenOffset(code, gaps = DEFAULT_GAPS) {
  const i = SCREEN_ORDER.indexOf(code?.toUpperCase());
  if (i < 0) return 0;
  return i * SCREEN_W + Math.ceil(i / 2) * gaps.bezel + Math.floor(i / 2) * gaps.corner;
}

export function worldWidth(gaps = DEFAULT_GAPS) {
  return 8 * SCREEN_W + 4 * gaps.bezel + 3 * gaps.corner;
}

/** Ring circumference: the unrolled strip closed into a loop by the 4th
 *  (D2→A1) corner. The last screen is physically adjacent to the first, so
 *  the cube can round that corner instead of ambling the long way back. */
export function ringCircumference(gaps = DEFAULT_GAPS) {
  return worldWidth(gaps) + gaps.corner;
}

const MARGIN = CUBE_SIZE / 2;

/* Constant-velocity circulation around the ring. The room loops, so there
 * are no ends to reflect off — the cube just keeps going and wraps at the
 * closing corner. (The old triangle-wave reflection made A1's left edge and
 * D2's right edge behave like walls the cube bounced off / stuck against.) */
function roamX(state, tSec, C) {
  let x = (state.x0 + state.vx0 * tSec) % C;
  if (x < 0) x += C;
  return { x, vx: state.vx0 };
}

/* Critically damped: x(t) = xT + (A + Bt)e^{-wt} — decel, reverse, no overshoot */
function steerX(state, tSec) {
  const A = state.x0 - state.xT;
  const B = state.vx0 + state.w * A;
  const e = Math.exp(-state.w * tSec);
  return {
    x: state.xT + (A + B * tSec) * e,
    vx: (B - state.w * (A + B * tSec)) * e,
  };
}

/* Periodic bounce off the floor with apex h: launch v = sqrt(2gh), period 2v/g */
function bounceState(tSec, h, g) {
  if (h <= 1) return { y: 0, vy: 0 };
  const v = Math.sqrt(2 * g * h);
  const T = (2 * v) / g;
  const tau = ((tSec % T) + T) % T;
  return { y: v * tau - 0.5 * g * tau * tau, vy: v - g * tau };
}

/** Evaluate cube position at server-clock ms. Returns { x, y, vx, h }. */
export function evaluate(state, serverNowMs, gaps = DEFAULT_GAPS) {
  const t = Math.max(0, (serverNowMs - state.t0) / 1000);
  const C = ringCircumference(gaps);

  let x; let vx;
  if (state.mode === 'steer' && state.xT !== null) {
    ({ x, vx } = steerX(state, t));
  } else {
    ({ x, vx } = roamX(state, t, C));
  }

  // Apex eases toward the touch height with the same damped envelope
  const h = state.mode === 'steer' && state.hT !== null
    ? state.hT + (state.h0 - state.hT) * Math.exp(-state.w * t)
    : state.h0;

  // phase0 keeps the bounce cycle continuous across retargets (no ground-slam)
  const { y, vy } = bounceState(t + (state.phase0 || 0), h, state.g);
  // The room is a ring: wrap x into [0, C) so roam circulates and a steer can
  // cross the D2→A1 seam. x below 0 reappears near C (last screen's far edge);
  // while x sits in the closing-corner gap (worldWidth..C) no screen shows the
  // cube — it's rounding the corner, exactly as at the other three.
  const xr = ((x % C) + C) % C;
  return { x: xr, y, vx, vy, h };
}

/** New state from a touch at world (x, y), evaluated at server-clock ms. */
export function stateFromTouch(state, serverNowMs, worldX, worldY, gaps = DEFAULT_GAPS) {
  const cur = evaluate(state, serverNowMs, gaps);
  // Target the tapped screen, but on the RING: take whichever arc is shorter.
  // If the long way round is what a plain x0→xT steer would do, retarget one
  // lap over (off-strip) so the damped steer crosses the D2↔A1 seam instead.
  // evaluate() wraps the off-strip target back for rendering.
  const C = ringCircumference(gaps);
  const rawT = Math.min(Math.max(worldX, MARGIN), worldWidth(gaps) - MARGIN);
  let d = (rawT - cur.x) % C;            // signed arc, folded to the short side
  if (d > C / 2) d -= C;
  else if (d < -C / 2) d += C;
  const xT = cur.x + d;

  // Bounce inertia survives the retarget: find the phase in the new cycle
  // that matches the cube's current height and vertical direction
  const g = state.g;
  const v0 = Math.sqrt(2 * g * Math.max(cur.h, 1));
  const yNow = Math.min(cur.y, cur.h);
  const root = Math.sqrt(Math.max(0, v0 * v0 - 2 * g * yNow));
  const phase0 = (cur.vy >= 0 ? v0 - root : v0 + root) / g;

  // Distance-scaled steering: aim for a slow cruise with soft ends, so a
  // long trip takes proportionally longer — never a fixed-duration warp.
  // The cube reacts SLOWLY on purpose: it considers, turns, and ambles over.
  const CRUISE = 240; // px/s — leisurely
  const travelT = Math.min(Math.max(Math.abs(xT - cur.x) / CRUISE, 2.2), 14);
  const w = 4.7 / travelT; // critically damped ~98% settled at t = 4.7/w

  return {
    ...state,
    mode: 'steer',
    t0: serverNowMs,
    x0: cur.x,
    vx0: cur.vx,
    xT,
    h0: cur.h,
    hT: Math.min(Math.max(worldY, 40), SCREEN_H - MARGIN),
    w,
    phase0,
  };
}
